import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import Placeholder from 'components/consumer/Placeholder';
import RatioContainer from 'components/consumer/RatioContainer';
import classNames from 'classnames';
import PDPNameSectionPlaceholder from './PDPNameSection/PDPNameSectionPlaceholder';
import PDPShopTabsBarPlaceholder from './PDPShopTabsBar/PDPShopTabsBarPlaceholder';
import PDPChooseYourOptionsPlaceholder from './PDPChooseYourOptions/PDPChooseYourOptionsPlaceholder';
import PDPProtectionPlanSelectorPlaceholder from './PDPProtectionPlanSelector/PDPProtectionPlanSelectorPlaceholder';
import PDPAddToBagSectionPlaceholder from './PDPAddToBagSection/PDPAddToBagSectionPlaceholder';
import buildPdpDesktopHeroLcpUrl from './PDPDesktopHeroGallery/helpers/buildPdpDesktopHeroLcpUrl';
import usePDPDeepLinkVariantMismatch from './hooks/usePDPDeepLinkVariantMismatch';
import { getPaintedHero } from './PDPDesktopHeroGallery/helpers/lastPaintedHero';

/**
 * Context-region Suspense fallback: section placeholders for the rail, and the
 * resolved server hero image (when available) so LCP can paint without waiting
 * on basicDetails / stock / protection / options.
 */
const PDPContextRegionPlaceholder = t0 => {
  const $ = _c(34);
  const {
    productHeroImage: t1,
    productCylindoHeroImage: t2,
    className: parentClassName
  } = t0;
  const productHeroImage = t1 === undefined ? null : t1;
  const productCylindoHeroImage = t2 === undefined ? null : t2;
  const t3 = productCylindoHeroImage?.defaultUpholsteryValue;
  const t4 = productCylindoHeroImage?.defaultOrientation;
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = {
      defaultUpholsteryValue: t3,
      defaultOrientation: t4
    };
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const isDeepLinkMismatch = usePDPDeepLinkVariantMismatch(t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getPaintedHero();
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const alreadyPainted = t6;
  const desktopHeroSrc = alreadyPainted || (isDeepLinkMismatch ? null : productHeroImage?.src || productCylindoHeroImage?.src || null);
  let t7;
  if ($[4] !== desktopHeroSrc) {
    t7 = desktopHeroSrc ? buildPdpDesktopHeroLcpUrl(desktopHeroSrc) ?? desktopHeroSrc : null;
    $[4] = desktopHeroSrc;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const desktopHeroHref = t7;
  const desktopHeroAlt = productHeroImage?.alt || productCylindoHeroImage?.alt || "";
  const mobileHeroSrc = alreadyPainted || (isDeepLinkMismatch ? null : productHeroImage?.src || productCylindoHeroImage?.src || null);
  const mobileHeroAlt = productHeroImage?.alt || productCylindoHeroImage?.alt || "";
  let t8;
  if ($[6] !== parentClassName) {
    t8 = classNames("hidden lg:flex mx-auto my-0 py-0 flex-row-reverse !pt-2 !pb-8 px-5 md:px-[3.333vw] min-[1500px]:px-[50px] max-w-[1800px]", parentClassName);
    $[6] = parentClassName;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex-none min-w-[460px] w-[460px] [@media(width:1024px)]:min-w-[400px] [@media(width:1024px)]:w-[400px]"><PDPNameSectionPlaceholder /><PDPShopTabsBarPlaceholder /><PDPChooseYourOptionsPlaceholder /><PDPProtectionPlanSelectorPlaceholder /><PDPAddToBagSectionPlaceholder /></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="min-h-[66px]" />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      maxWidth: "calc((100vh - 194px) / 0.6666666666666666)"
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== desktopHeroAlt || $[12] !== desktopHeroHref || $[13] !== productHeroImage?.height || $[14] !== productHeroImage?.width) {
    t12 = <div className="flex-1 mr-12">{t10}<div className="sticky top-8"><div className="mb-8 ml-auto mr-auto relative" style={t11}><RatioContainer ratio={0.6666666666666666}>{desktopHeroHref ? <img className="h-full w-full object-contain object-center select-none" src={desktopHeroHref} alt={desktopHeroAlt} width={productHeroImage?.width || 1600} height={productHeroImage?.height || 2400} loading="eager" fetchPriority="high" decoding="async" /> : <Placeholder loading={true} type="rect" />}</RatioContainer></div></div></div>;
    $[11] = desktopHeroAlt;
    $[12] = desktopHeroHref;
    $[13] = productHeroImage?.height;
    $[14] = productHeroImage?.width;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12 || $[17] !== t8) {
    t13 = <div className={t8} data-xc="PDPContextRegionPlaceholder-desktop">{t9}{t12}</div>;
    $[16] = t12;
    $[17] = t8;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== parentClassName) {
    t14 = classNames("flex flex-col lg:hidden", parentClassName);
    $[19] = parentClassName;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="pl-5 pr-5 md:pl-[3.333vw] md:pr-[3.333vw]"><PDPNameSectionPlaceholder /></div>;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== mobileHeroAlt || $[23] !== mobileHeroSrc || $[24] !== productHeroImage?.height || $[25] !== productHeroImage?.width) {
    t16 = mobileHeroSrc ? <img className="mb-1.5 w-full object-contain object-center" src={mobileHeroSrc} alt={mobileHeroAlt} width={productHeroImage?.width || 800} height={productHeroImage?.height || 800} loading="eager" fetchPriority="high" decoding="async" style={{
      width: "100vw",
      aspectRatio: "1/1",
      objectFit: "contain"
    }} /> : <Placeholder className="mb-1.5" loading={true} type="rect" style={{
      width: "100vw",
      aspectRatio: "1/1"
    }} />;
    $[22] = mobileHeroAlt;
    $[23] = mobileHeroSrc;
    $[24] = productHeroImage?.height;
    $[25] = productHeroImage?.width;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="pl-5 pr-5 md:pl-[3.333vw] md:pr-[3.333vw]"><PDPChooseYourOptionsPlaceholder /><PDPProtectionPlanSelectorPlaceholder /><PDPAddToBagSectionPlaceholder /></div>;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t14 || $[29] !== t16) {
    t18 = <div className={t14} data-xc="PDPContextRegionPlaceholder-mobile">{t15}{t16}{t17}</div>;
    $[28] = t14;
    $[29] = t16;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t13 || $[32] !== t18) {
    t19 = <>{t13}{t18}</>;
    $[31] = t13;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  return t19;
};
PDPContextRegionPlaceholder.propTypes = {
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  productCylindoHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string
  }),
  className: PropTypes.string
};
export default PDPContextRegionPlaceholder;